import * as React from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form } from '@/components/ui/form';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { ClientIntegration, ExternalClient } from '@/modules/clients/types';
import { SingleSelect } from '@/modules/common';

interface ClientIntegrationsSelectCellProps {
  integration: ClientIntegration;
  onChange: (integrationId: string, clientId?: string) => void;
  isReadMode: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}

const NOT_SELECTED_VALUE = '__not_selected__';
const FORM_FIELD_SELECTED_CLIENT_ID = 'selectedClientId';

export const ClientIntegrationsSelectCell: React.FC<ClientIntegrationsSelectCellProps> = ({
  integration,
  onChange,
  isReadMode,
  t,
}) => {
  const form = useForm({
    defaultValues: {
      [FORM_FIELD_SELECTED_CLIENT_ID]: integration.selectedValue?.id || NOT_SELECTED_VALUE,
    },
  });

  // Watch form changes
  const selectedClientId = form.watch(FORM_FIELD_SELECTED_CLIENT_ID);

  // Use ref to store onChange to avoid it being in dependency arrays
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Track the last value from integration to detect external changes
  const lastIntegrationValueRef = React.useRef<string>(
    integration.selectedValue?.id || NOT_SELECTED_VALUE
  );

  // Update form when integration changes externally (from parent state updates)
  React.useEffect(() => {
    const newValue = integration.selectedValue?.id || NOT_SELECTED_VALUE;
    const lastValue = lastIntegrationValueRef.current;

    // Only update form if integration value changed externally (not from our own onChange)
    if (newValue !== lastValue) {
      lastIntegrationValueRef.current = newValue;
      form.setValue(FORM_FIELD_SELECTED_CLIENT_ID, newValue, { shouldDirty: false });
    }
  }, [integration.selectedValue?.id, form]);

  // Trigger callback when user makes a selection
  React.useEffect(() => {
    const currentIntegrationValue = integration.selectedValue?.id || NOT_SELECTED_VALUE;

    // Only trigger onChange if form value differs from integration's current value
    // This means the user made a change in the UI
    if (selectedClientId !== currentIntegrationValue) {
      // Update our tracking ref
      lastIntegrationValueRef.current = selectedClientId;
      // Convert special "not selected" value to undefined
      const clientId = selectedClientId === NOT_SELECTED_VALUE ? undefined : selectedClientId;
      onChangeRef.current(integration.id, clientId);
    }
  }, [selectedClientId, integration.id, integration.selectedValue?.id]);

  // Build options with "Not selected" as first option
  const clientOptions = React.useMemo(() => {
    const notSelectedOption = {
      value: NOT_SELECTED_VALUE,
      label: t('details.tabs.integrations.notSelected'),
    };

    const mappedClients = (integration.clients || []).map((client: ExternalClient) => ({
      value: client.id,
      label: client.name,
    }));

    return [notSelectedOption, ...mappedClients];
  }, [integration.clients, t]);

  const isDisabled = isReadMode || (integration.clients?.length || 0) === 0;

  const selectElement = (
    <div className="w-full max-w-xs">
      <Form {...form}>
        <SingleSelect
          name={FORM_FIELD_SELECTED_CLIENT_ID}
          control={form.control}
          placeholder={
            integration.allClientsLinked
              ? t('details.tabs.integrations.notSelected')
              : (integration.clients?.length || 0) === 0
                ? t('details.tabs.integrations.noClientsAvailable')
                : t('details.tabs.integrations.selectClient')
          }
          options={clientOptions}
          disabled={isDisabled}
          searchable={true}
          showDescriptionInOptions={false}
          showDescriptionInSelector={false}
        />
      </Form>
    </div>
  );

  if (integration.allClientsLinked) {
    return (
      <SimpleTooltip
        content={t('details.tabs.integrations.allClientsLinkedTooltip', {
          integrationName: integration.type,
        })}
      >
        {selectElement}
      </SimpleTooltip>
    );
  }

  return selectElement;
};
